import dayjs from 'dayjs'

/**
 * Convert code string to readable label
 * Example: "SOCIAL_HABITS" → "Social Habits"
 */
export const formatLabel = (code) => {
  if (!code) return ''

  return code
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Check if EMR value has actual data
 */
export const hasValue = (emrValue) => {
  if (!emrValue) return false

  // SELECT_ONE: Check code/display
  if (emrValue.code || emrValue.display) return true

  // SELECT_MULTI: Check valueStringList (EmrValueItem[])
  if (Array.isArray(emrValue.valueStringList) && emrValue.valueStringList.length > 0) {
    return true
  }

  // TEXT/TEXTAREA
  if (emrValue.valueString) return true

  // NUMBER
  if (emrValue.valueNumber !== null && emrValue.valueNumber !== undefined) return true

  // BOOLEAN
  if (emrValue.valueBoolean !== null && emrValue.valueBoolean !== undefined) return true

  // DATE/TIME/DATETIME
  if (emrValue.valueDate) return true

  return false
}

/**
 * Format EMR value to display string
 */
export const formatEmrValue = (emrValue) => {
  if (!emrValue) return ''

  // SELECT_ONE: Use display if available, fallback to code
  if (emrValue.code || emrValue.display) {
    return emrValue.display || formatLabel(emrValue.code)
  }

  // SELECT_MULTI: Handle EmrValueItem[] structure
  if (Array.isArray(emrValue.valueStringList) && emrValue.valueStringList.length > 0) {
    const displayValues = emrValue.valueStringList.map((item) => {
      // Check if item is EmrValueItem object with display/code
      if (typeof item === 'object' && item !== null) {
        return item.display || formatLabel(item.code) || String(item)
      }
      // Fallback for string values
      return String(item)
    })
    return displayValues.join(', ')
  }

  // TEXT/TEXTAREA
  if (emrValue.valueString) {
    return emrValue.valueString
  }

  // NUMBER: Include unit if available
  if (emrValue.valueNumber !== null && emrValue.valueNumber !== undefined) {
    const numberStr = String(emrValue.valueNumber)
    return emrValue.unit ? `${numberStr} ${emrValue.unit}` : numberStr
  }

  // BOOLEAN
  if (emrValue.valueBoolean !== null && emrValue.valueBoolean !== undefined) {
    return emrValue.valueBoolean ? 'Yes' : 'No'
  }

  // DATE/DATETIME/TIME
  if (emrValue.valueDate) {
    return formatDateTime(emrValue.valueDate)
  }

  return ''
}

/**
 * Format date/time values based on content
 * Detects if it's date-only, time-only, or datetime and formats accordingly
 */
export const formatDateTime = (dateValue) => {
  if (!dateValue) return ''

  const date = dayjs(dateValue)
  if (!date.isValid()) {
    return String(dateValue)
  }

  // Check if it's time-only (no date component or date is epoch/min date)
  const hasDateComponent = !(
    date.year() === 1970 ||
    date.year() === 1900 ||
    date.format('YYYY-MM-DD') === '1970-01-01'
  )

  // Check if it has time component (not midnight)
  const hasTimeComponent =
    date.hours() !== 0 || date.minutes() !== 0 || date.seconds() !== 0 || date.milliseconds() !== 0

  if (hasDateComponent && hasTimeComponent) {
    // Full datetime
    return date.format('DD/MM/YYYY hh:mm A')
  } else if (hasDateComponent && !hasTimeComponent) {
    // Date only
    return date.format('DD/MM/YYYY')
  } else if (!hasDateComponent && hasTimeComponent) {
    // Time only
    return date.format('hh:mm A')
  }

  // Default: date only
  return date.format('DD/MM/YYYY')
}

